import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { t } from 'ttag';
import classNames from 'classnames';
import * as ReactModal from 'react-modal';

import MenuIcon from '@svg/menu.svg';
import ArrowRightIcon from '@svg/arrows-right.svg';
import DiscountIcon from '@svg/discount.svg';
import { dayDiscount, socialFeed } from '@urls';
import { useAppContext } from '@contexts/AppContext';
import { useDisableBodyScrollEffect } from '@hook/useDisableBodyScrollEffect';
import { Link } from '@ui/Link';
import { ShafaLogo } from '@ui/ShafaLogo';
import { LocalizedLink } from '@ui/LocalizedLink';
import { Skeleton } from '@ui/Skeleton';
import { LanguageToggle } from '@components/LanguageToggle';
import { NAMESPACE_ICONS_MAP } from '@components/Header/utils';

import s from './HeaderCatalogMenuMobile.sss';

export type Namespace = {
  namespace?: string;
};

const useLevelController = () => {
  const { current: history } = useRef<CatalogTree[] & Namespace>([]);
  const [currentNode, setCurrentNode] = useState<CatalogTree & Namespace | null>(null);
  const go = (node: CatalogTree & Namespace) => {
    let namespace;
    // If we currently on non root node, add it to history
    if (currentNode) {
      history.push(currentNode);
      namespace = currentNode.namespace;
    } else {
      namespace = node.slug;
    }
    setCurrentNode({ ...node, namespace });
  };

  const back = () => {
    // If there is no nodes in history to go back, go to the root level
    if (!history.length) {
      setCurrentNode(null);
    } else {
      // Go to the previous level in history and remove it from history
      setCurrentNode(history.pop()!);
    }
  };

  return {
    go,
    level: currentNode,
    back: currentNode !== null
      ? back
      : undefined,
    backToRoot: () => setCurrentNode(null),
  };
};

interface StaticLink {
  title: string,
  icon: React.FC<React.SVGProps<SVGAElement>>,
  href: string,
}

export interface CatalogTree {
  id: number,
  name: string,
  slug: string,
  subCatalogs: CatalogTree[],
}

interface Props {
  isOpen: boolean,
  catalogTree?: CatalogTree | null,
  staticLinks: StaticLink[],
  isSalesLinkEnabled?: boolean,
  isCatalogTreeLoading?: boolean,
  onClose: () => void,
}

export const HeaderCatalogMenuMobile: React.FC<Props> = ({
  isOpen,
  catalogTree,
  staticLinks,
  isSalesLinkEnabled = true,
  isCatalogTreeLoading,
  onClose,
}) => {
  const history = useHistory();
  const createHref = (url: string) => history.createHref({ pathname: url });
  const { location } = history;
  const { level, go, back, backToRoot } = useLevelController();
  const { isOutlet } = useAppContext();

  useDisableBodyScrollEffect(isOpen);

  return (
    <ReactModal
      ariaHideApp={false}
      className={{
        base: s.root,
        afterOpen: s.afterOpen,
        beforeClose: s.beforeClose,
      }}
      overlayClassName={{
        base: s.overlay,
        afterOpen: s.afterOpen,
        beforeClose: s.beforeClose,
      }}
      htmlOpenClassName={s.htmlOpen}
      bodyOpenClassName={s.bodyOpen}
      closeTimeoutMS={300}
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <div className={s.head}>
        {level === null ? (
          <>
            <div className={s.leftSide}>
              <MenuIcon
                className={s.burger}
                onClick={onClose}
              />
              <ShafaLogo
                width="64px"
                height="16px"
              />
            </div>
            <div className={s.rightSide}>
              <LanguageToggle pathname={location.pathname} />
            </div>
          </>
        ) : (
          <div
            className={s.activeLevel}
            onClick={back}
          >
            <ArrowRightIcon
              className={s.arrowBack}
              width='24'
              height='24'
            />
            {level.name}
          </div>
        )}
      </div>

      <ul className={s.list}>
        {level && (
          <li className={s.item}>
            <Link
              className={s.levelLink}
              href={
                level.namespace === level.slug
                  ? createHref(`/${level.namespace}`)
                  : createHref(`/${level.namespace}/${level.slug}`)
              }
              onClick={(e) => {
                if (isOutlet) {
                  e.preventDefault();

                  history.push(
                    level.namespace === level.slug
                      ? `/${level.namespace}`
                      : `/${level.namespace}/${level.slug}`,
                  );
                  backToRoot();
                  onClose();
                }
              }}
            >
              {t`Все у ${level.name}`}
            </Link>
          </li>
        )}

        {!level && isSalesLinkEnabled && (
          <li className={s.item}>
            <LocalizedLink
              className={classNames(s.link, s.linkThemePink)}
              href={dayDiscount}
            >
              <DiscountIcon
                className={s.icon}
                width='24'
                height='24'
              />
              {t`Знижки`}
            </LocalizedLink>
          </li>
        )}

        {isCatalogTreeLoading ? (
          Array(10).fill(null).map((_, i) => (
            <li
              key={i}
              className={s.item}
            >
              <Skeleton
                width='100%'
                height='48px'
              />
            </li>
          ))
        ) : (
          (level?.subCatalogs || catalogTree?.subCatalogs || []).map((levelChild) => {
            const Icon = NAMESPACE_ICONS_MAP[levelChild.slug];
  
            return (
              <li
                key={levelChild.name}
                className={s.item}
              >
                <Link
                  theme='black'
                  className={s.link}
                  href={level ? createHref(`/${level.namespace}/${levelChild.slug}`) : createHref(`/${levelChild.slug}`)}
                  onClick={(e) => {
                    if (levelChild.subCatalogs?.length) {
                      e.preventDefault();
                      go(levelChild);
                    } else if (isOutlet) {
                      e.preventDefault();
              
                      history.push(
                        level
                          ? `/${level.namespace}/${levelChild.slug}`
                          : `/${levelChild.slug}`,
                      );
                      backToRoot();
                      onClose();
                    }
                  }}
                >
                  <div className={s.leftSide}>
                    {!!Icon && (
                      <Icon
                        className={s.icon}
                        width='24'
                        height='24'
                      />
                    )}
    
                    {levelChild.name}
                  </div>
    
                  {!!levelChild?.subCatalogs?.length && (
                    <ArrowRightIcon
                      className={s.arrow}
                      width='24'
                      height='24'
                    />
                  )}
                </Link>
              </li>
            );
          })
        )}

        {!level && (
          <>
            {staticLinks.map((link, i) => {
              const Icon = link.icon;

              return (
                <li
                  key={i}
                  className={s.item}
                >
                  <Link
                    className={s.link}
                    theme="black"
                    href={link.href}
                  >
                    <Icon
                      className={classNames(s.icon, {
                        [s.iconTypeSocialFeed]: link.href.includes(socialFeed), // TODO: a.poleliuk replace the icon
                      })}
                      width='24'
                      height='24'
                    />
                    {link.title}
                  </Link>
                </li>
              );
            })}
          </>
        )}
      </ul>
    </ReactModal>
  );
};
